import { Button, Card, TextInput } from "@tremor/react";
import { useEffect, useState } from "react";
import { usePermission } from "../lib/usePermission.js";
import { toast } from "sonner";
import { fetchSlackAuthorizeUrl } from "../api/slack.js";
import {
  useCurrentWorkspace,
  useUpdateCurrentWorkspace,
} from "../queries/workspaces.js";
import {
  useSlackInstallation,
  useUpdateSlackInstallation,
  useDeleteSlackInstallation,
} from "../queries/misc.js";
import { useTranslation } from "react-i18next";

function Section({ title, children }) {
  return (
    <Card className="p-0">
      <div className="border-b border-gray-100 px-5 py-4">
        <h2 className="text-sm font-semibold text-gray-700">{title}</h2>
      </div>
      <div className="px-5 py-4">{children}</div>
    </Card>
  );
}

function SlackSection() {
  const { t } = useTranslation("settings");
  const { data: installation, isLoading } = useSlackInstallation();
  const updateMut = useUpdateSlackInstallation();
  const deleteMut = useDeleteSlackInstallation();

  useEffect(() => {
    // Handle ?slack=connected / ?slack=error query params
    const params = new URLSearchParams(window.location.search);
    const slackParam = params.get("slack");
    if (slackParam === "connected") {
      toast.success(t("slack.success_connected"));
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (slackParam === "error") {
      toast.error(t("slack.error_connection"));
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [t]);

  async function handleDisconnect() {
    if (!window.confirm(t("slack.disconnect_confirm"))) return;
    try {
      await deleteMut.mutateAsync();
      toast.success(t("slack.success_disconnect"));
    } catch {
      toast.error("Failed to disconnect Slack.");
    }
  }

  async function handleChannelChange(e) {
    e.preventDefault();
    const channelName = e.target.elements.channel.value.trim().replace(/^#/, "");
    if (!channelName) return;
    try {
      await updateMut.mutateAsync({ default_channel_name: `#${channelName}` });
      toast.success(t("slack.success_channel"));
    } catch {
      toast.error("Failed to update channel.");
    }
  }

  if (isLoading) {
    return <p className="text-sm text-gray-400">{t("loading")}</p>;
  }
  const deleting = deleteMut.isPending;

  async function handleConnect() {
    try {
      const url = await fetchSlackAuthorizeUrl();
      window.location.assign(url);
    } catch {
      toast.error("Could not start Slack connection. Please try again.");
    }
  }

  if (!installation) {
    return (
      <div className="flex flex-col gap-3">
        <p className="text-sm text-gray-600">
          {t("slack.connect_info")}
        </p>
        <div>
          <button
            type="button"
            onClick={handleConnect}
            className="inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm transition hover:bg-gray-50"
          >
            <SlackIcon />
            {t("slack.connect_button")}
          </button>
        </div>
        <p className="text-xs text-gray-400">
          {t("slack.authorize_info")}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <SlackIcon />
        <div>
          <p className="text-sm font-medium text-gray-800">{installation.team_name}</p>
          <p className="text-xs text-gray-400">{t("slack.connected_workspace")}</p>
        </div>
        <div className="ml-auto">
          <Button variant="secondary" size="xs" color="red" disabled={deleting} onClick={handleDisconnect}>
            {deleting ? t("slack.disconnecting") : t("slack.disconnect")}
          </Button>
        </div>
      </div>

      <form onSubmit={handleChannelChange} className="flex items-end gap-3">
        <div className="flex-1">
          <label className="mb-1 block text-xs font-medium text-gray-600">
            {t("slack.channel_label")}
          </label>
          <TextInput
            name="channel"
            placeholder="#incidents"
            defaultValue={installation.default_channel_name ?? ""}
          />
        </div>
        <Button type="submit" size="sm">{t("common:common.save")}</Button>
      </form>
      <p className="text-xs text-gray-400">
        {t("slack.channel_info")}
      </p>
    </div>
  );
}

function SlackIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 270 270" aria-hidden="true">
      <path d="M99.4 151.2c0 7.1-5.8 12.9-12.9 12.9-7.1 0-12.9-5.8-12.9-12.9 0-7.1 5.8-12.9 12.9-12.9h12.9v12.9z" fill="#E01E5A"/>
      <path d="M105.9 151.2c0-7.1 5.8-12.9 12.9-12.9s12.9 5.8 12.9 12.9v32.3c0 7.1-5.8 12.9-12.9 12.9s-12.9-5.8-12.9-12.9v-32.3z" fill="#E01E5A"/>
      <path d="M118.8 99.4c-7.1 0-12.9-5.8-12.9-12.9 0-7.1 5.8-12.9 12.9-12.9 7.1 0 12.9 5.8 12.9 12.9v12.9h-12.9z" fill="#36C5F0"/>
      <path d="M118.8 105.9c7.1 0 12.9 5.8 12.9 12.9s-5.8 12.9-12.9 12.9H86.5c-7.1 0-12.9-5.8-12.9-12.9s5.8-12.9 12.9-12.9h32.3z" fill="#36C5F0"/>
      <path d="M170.6 118.8c0-7.1 5.8-12.9 12.9-12.9 7.1 0 12.9 5.8 12.9 12.9 0 7.1-5.8 12.9-12.9 12.9h-12.9v-12.9z" fill="#2EB67D"/>
      <path d="M164.1 118.8c0 7.1-5.8 12.9-12.9 12.9s-12.9-5.8-12.9-12.9V86.5c0-7.1 5.8-12.9 12.9-12.9s12.9 5.8 12.9 12.9v32.3z" fill="#2EB67D"/>
      <path d="M151.2 170.6c7.1 0 12.9 5.8 12.9 12.9 0 7.1-5.8 12.9-12.9 12.9-7.1 0-12.9-5.8-12.9-12.9v-12.9h12.9z" fill="#ECB22E"/>
      <path d="M151.2 164.1c-7.1 0-12.9-5.8-12.9-12.9s5.8-12.9 12.9-12.9h32.3c7.1 0 12.9 5.8 12.9 12.9s-5.8 12.9-12.9 12.9h-32.3z" fill="#ECB22E"/>
    </svg>
  );
}

const SLUG_RE = /^[a-z0-9][a-z0-9-]{1,61}[a-z0-9]$/;

function toSlug(s) {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export default function Settings() {
  const { t } = useTranslation(["settings", "common"]);
  const can = usePermission();
  const { data: workspace, isLoading: loading } = useCurrentWorkspace();
  const updateWs = useUpdateCurrentWorkspace();
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugError, setSlugError] = useState("");
  const [savingName, setSavingName] = useState(false);
  const [savingSlug, setSavingSlug] = useState(false);

  useEffect(() => {
    if (workspace) {
      setName(workspace.name);
      setSlug(workspace.slug);
    }
  }, [workspace]);

  async function handleSaveName(e) {
    e.preventDefault();
    if (!workspace || !name.trim() || name === workspace.name) return;

    setSavingName(true);
    try {
      await updateWs.mutateAsync({ name: name.trim() });
      toast.success(t("general.success_name"));
    } catch {
      toast.error("Failed to update workspace name");
    } finally {
      setSavingName(false);
    }
  }

  function handleSlugChange(v) {
    setSlug(v);
    setSlugError("");
  }

  async function handleSaveSlug(e) {
    e.preventDefault();
    if (!workspace || !slug.trim() || slug === workspace.slug) return;

    if (!SLUG_RE.test(slug)) {
      setSlugError(t("errors.slug_invalid"));
      return;
    }

    setSavingSlug(true);
    try {
      await updateWs.mutateAsync({ slug: slug.trim() });
      toast.success(t("general.success_slug"));
    } catch (err) {
      const msg = err?.response?.data?.error;
      if (err?.response?.status === 409) {
        setSlugError(t("errors.slug_taken"));
      } else {
        toast.error(msg || "Failed to update slug");
      }
    } finally {
      setSavingSlug(false);
    }
  }

  if (loading) {
    return (
      <div>
        <h1 className="text-2xl font-bold text-gray-900">{t("title")}</h1>
        <p className="mt-1 text-sm text-gray-500">{t("subtitle")}</p>
        <p className="mt-4 text-sm text-gray-400">{t("loading")}</p>
      </div>
    );
  }

  const slugChanged = slug !== workspace.slug;
  const slugValid = SLUG_RE.test(slug);

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">{t("title")}</h1>
        <p className="mt-1 text-sm text-gray-500">{t("subtitle")}</p>
      </div>

      <div className="space-y-6">
        <Section title={t("slack.title")}>
          <SlackSection />
        </Section>
        <Section title={t("general.title")}>
          {can("settings.edit") ? (
            <div className="space-y-6">
              <form onSubmit={handleSaveName} className="flex items-end gap-3">
                <div className="flex-1">
                  <label className="mb-1 block text-xs font-medium text-gray-600">
                    {t("general.workspace_name")}
                  </label>
                  <TextInput value={name} onValueChange={setName} />
                </div>
                <Button
                  type="submit"
                  disabled={savingName || !name.trim() || name === workspace.name}
                >
                  {savingName ? t("general.saving") : t("general.save")}
                </Button>
              </form>

              <form onSubmit={handleSaveSlug}>
                <div className="mb-1 flex items-end gap-3">
                  <div className="flex-1">
                    <label className="mb-1 block text-xs font-medium text-gray-600">
                      {t("general.slug")}
                    </label>
                    <TextInput
                      value={slug}
                      onValueChange={handleSlugChange}
                      placeholder={t("general.slug_placeholder")}
                      error={!!slugError}
                      errorMessage={slugError}
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={savingSlug || !slugChanged || !slugValid}
                  >
                    {savingSlug ? t("general.saving") : t("general.save")}
                  </Button>
                </div>
                {slugChanged && (
                  <p className="mt-2 text-xs text-amber-600">
                    {t("general.slug_change_info", { old: workspace.slug, new: slug || "…" })}
                  </p>
                )}
                {!slugChanged && (
                  <p className="mt-1 text-xs text-gray-400">
                    {t("general.dashboard_url_info", { url: workspace.slug })}
                  </p>
                )}
              </form>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <p className="text-xs font-medium text-gray-400">{t("general.workspace_name")}</p>
                <p className="mt-0.5 text-gray-700">{workspace.name}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-gray-400">{t("general.slug")}</p>
                <p className="mt-0.5 font-mono text-gray-700">{workspace.slug}</p>
              </div>
            </div>
          )}
        </Section>
      </div>
    </div>
  );
}
